import React, { useState } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Button,
    Grid,
    InputLabel,
    TextareaAutosize,
    Typography
} from '@mui/material';
import TextFieldWithPrefix from "../../components/TextFieldWithPrefix/TextFieldWithPrefix"
import { FormGeneratorProps, ButtonField } from '../../types/types';
import { grey } from '@mui/material/colors'
import styles from './FormeBuilder.module.scss';


const FormGenerator = <T extends Record<string, any>>({
    fields,
    onSubmit,
    showSubmit = true,
    submitLabel = 'Submit',
    direction = 'column',
    buttonColor = 'success',
    ButtonList = []
}: FormGeneratorProps<T>) => {
    // Initial state from defaults
    const initialValues = fields.reduce((acc, field) => {
        acc[field.name] =
            field.defaultValue ??
            (field.type === 'checkbox' ? false : '');
        return acc;
    }, {} as T);

    const [formData, setFormData] = useState<T>(initialValues);
    const [errors, setErrors] = useState<Record<string, string | boolean>>({});

    const validateField = (
        name: string,
        value: any,
        validation?: (val: any) => string | undefined | boolean
    ) => {
        let errorMsg: string = '';

        if (validation) {
            const result = validation(value);
            errorMsg = typeof result === 'string' ? result : '';
        }

        setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const now = new Date();
        const { name, type, value, } = e.target;
        let updatedValue: string | number | boolean | null;
        if (type === "date") {
            const updatedDate = new Date(`${value}T${now.toTimeString().split(' ')[0]}`);
            if (isNaN(updatedDate.getTime())) {
                console.warn(`[Date Warning] Invalid date input received for: ${name}`, value);
                updatedValue = null;
            } else {
                updatedValue = updatedDate.toISOString();
            }
        } else {

            updatedValue = type === 'checkbox' && e.target instanceof HTMLInputElement
                ? e.target.checked
                : type === 'number' ? Number(value) : value;

        }


        setFormData((prev) => ({
            ...prev,
            [name]: updatedValue,
        }));

        const field = fields.find(f => f.name === name);
        if (field?.validate) {
            validateField(name, updatedValue, field.validate);
        }
    };

    const formValidation = () => {
        let hasError = false;
        const newErrors: Record<string, string | boolean> = {};
        fields.forEach(({ name, validate, type, options = [] }) => {
            if (validate) {
                if (type === "textWithDropDown") {
                    let optionValid = false;

                    for (const data of options) {
                        const value = formData[data.value];
                        if (value && !validate(value)) {
                            // Valid input found
                            optionValid = true;
                            break;
                        }
                    }

                    if (!optionValid) {
                        // All options either missing or invalid
                        const firstOptionWithValue = options.find(opt => formData[opt.value]);
                        const errorMsg = firstOptionWithValue
                            ? validate(formData[firstOptionWithValue.value])
                            : "This field is required"; // or a default error message
                        if (errorMsg) {
                            newErrors[name] = errorMsg;
                            hasError = true;
                        }

                    }
                }
                else {
                    const errorMsg = validate(formData[name]);

                    if (errorMsg) {
                        newErrors[name] = errorMsg;
                        hasError = true;
                    }
                }
            }
        });

        setErrors(newErrors);
        return hasError
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        let hasError = formValidation();

        if (!hasError) onSubmit(formData);
    };

    const HandleButtonClick = (list: ButtonField) => {
        let hasError = formValidation();
        if (!hasError) {
            list.onClick(formData)
        }
    }
    const HandleTextfield = (field: any, data: any) => {
        setFormData((prev) => ({
            ...prev,
            [data.prefix]: data.text,
        }));


        if (field?.validate) {
            validateField(data.prefix, data.text, field.validate);
        }
    }
    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: direction, gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}
        >
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                {fields.map((field, index) => {
                    const value = formData[field.name];
                    const gridProps = field.grid ?? { xs: 12, sm: 6 }; // Default 2 fields per row
                    const { name,inputProps, disabled = false, type, options, defaultValue, text_label, label, required, grid = { xs: 12, sm: 6 } } = field;
                    const error = errors[name]
                    const filedMapKey = field.dropdownKeyMap;
                    const commonProps = {
                        type,
                        name,
                        required,
                        defaultValue: defaultValue,
                        className: styles.customTextfield,
                        disabled,
                        variant: 'filled' as const,
                        onChange: handleChange,
                        onBlur: () => validateField(field.name, formData[field.name], field.validate)

                    };
                    return (
                        <Grid key={field.name} size={gridProps}>
                            <InputLabel shrink sx={{ fontSize: "1rem", fontFamily: 'Poppins, sans-serif' }} error={Boolean(error)} required={field?.validate && true}>{text_label}</InputLabel>
                            {type === "textWithDropDown" ?
                                <>
                                    <TextFieldWithPrefix
                                        prefixOptions={options}
                                        onChange={(data) => HandleTextfield(field, data)}
                                        ShowIcon={false}
                                        selectClassName={styles.customTextwithDropSelectfield}
                                        inputClassName={styles.customTextwithDropInputfield}
                                        variant="filled"
                                        defaultValue={defaultValue} 
                                        slectedPrefix={filedMapKey} /> {error && <Typography variant='caption' color='error'>{error}</Typography>}</> :
                                type === "select" && Array.isArray(options) ? <> <TextField
                                    fullWidth
                                    key={field.name}
                                    select
                                    error={Boolean(error)}
                                    {...commonProps}
                                >
                                    {options.map((option) => {
                                        
                                        const isObject = typeof option === "object";

                                        const optionValue = isObject
                                        ? option.allocation_id
                                        : option;
                                        const optionLabel = isObject
                                        ? option.purpose
                                        : option;
                                        const isSelected = value === optionValue;

                                        return (
                                        <MenuItem key={optionValue} value={optionValue} selected={isSelected}>
                                            {optionLabel}
                                        </MenuItem>
                                        );
                                    })}
                                </TextField> {error && <Typography variant='caption' color='error'>{error}</Typography>}
                                    {field.subText && <Typography variant='caption' color='info'>{field.subText}</Typography>}
                                </> : type === 'checkbox' ? <FormControlLabel
                                    key={name}
                                    control={
                                        <Checkbox
                                            checked={Boolean(defaultValue || value)}
                                            {...commonProps}
                                        />
                                    }
                                    label={label}
                                /> : type === 'textArea' ? (
                                    <>
                                        <TextareaAutosize
                                            {...commonProps}
                                            key={name}
                                            aria-label={type}
                                            minRows={3}
                                            defaultValue={defaultValue}
                                            style={{ width: '100%', borderColor: grey[500], marginBottom: 4, padding: 16, height: 50, borderRadius: "14px" }}
                                        />
                                        {field.subText && <Typography variant='caption' color='info'>{field.subText}</Typography>}
                                        {error && <Typography variant='caption' color='error'>{error}</Typography>}
                                    </>
                                ) : type === 'button' ? (
                                    <div>
                                        <Button disabled={disabled} variant="contained" fullWidth onClick={() => field.onClick && field.onClick(field)} sx={{ color: "#000", backgroundColor: grey[100], borderRadius: 2, padding: "0.78rem", }}>{text_label} </Button>
                                        {field.subText && <Typography variant='caption' color='info'>{field.subText}</Typography>}
                                    </div>

                                ) :
                                    (<div>
                                        <TextField
                                            fullWidth
                                            error={true}
                                            key={name}
                                            // value={value}
                                            {...commonProps}
                                            helperText={error}
                                            inputProps = {inputProps}
                                        />
                                        {field.subText && <Typography variant='caption' color='info'>{field.subText}</Typography>}
                                    </div>
                                    )}
                        </Grid>
                    );
                }
                )}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }} >
                {ButtonList.length > 0 ? ButtonList.map((list, index) => <Button key={index} onClick={() => HandleButtonClick(list)} variant="contained" color={list.ButtonColor} disabled={list.disabled} sx={{ maxWidth: "150px" }}>
                    {list.name}
                </Button>) : showSubmit && <Button type="submit" variant="contained" color={buttonColor} sx={{ maxWidth: "150px" }}>
                    {submitLabel}
                </Button>}
            </Box>
        </Box>
    );
};

export default FormGenerator;
